/**
 * Warms product documents and leading PDP images shortly before navigation.
 * Desktop uses pointer/focus intent; touch devices warm a small number of
 * nearby cards while idle so a tap does not start from a cold cache.
 */
(function () {
  const CARD_SELECTOR = '[data-gullyo-product-card], product-card';
  const PRODUCT_LINK_SELECTOR = 'a[data-gullyo-card-link], a[ref="productCardLink"]';
  const MAX_TOUCH_WARMUPS = 2;
  const MAX_WARMED_PRODUCTS = 8;
  const HOVER_DELAY_MS = 80;
  const PDP_IMAGE_SIZES = '(min-width: 750px) 50vw, 100vw';

  const connection =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const effectiveType = String(connection?.effectiveType || '');
  const constrainedNetwork =
    connection?.saveData === true || effectiveType === 'slow-2g' || effectiveType === '2g';

  if (constrainedNetwork) {
    document.documentElement.dataset.gullyoProductPrefetch = 'network-constrained';
    return;
  }

  document.documentElement.dataset.gullyoProductPrefetch = 'ready';

  const warmedDocuments = new Map();
  const warmedImages = new Map();
  const warmedProductOrder = [];
  let hoverTimer = 0;
  let hoverCard = null;

  function rememberWarmedProduct(productUrl) {
    const href = productUrl.href;
    const existingIndex = warmedProductOrder.indexOf(href);
    if (existingIndex !== -1) {
      warmedProductOrder.splice(existingIndex, 1);
    }
    warmedProductOrder.push(href);

    while (warmedProductOrder.length > MAX_WARMED_PRODUCTS) {
      const expiredHref = warmedProductOrder.shift();
      if (!expiredHref) continue;

      warmedDocuments.get(expiredHref)?.remove();
      warmedDocuments.delete(expiredHref);
      warmedImages.delete(expiredHref);
    }
  }

  function getCard(target) {
    return target instanceof Element ? target.closest(CARD_SELECTOR) : null;
  }

  function getProductLink(card) {
    const link = card?.querySelector(PRODUCT_LINK_SELECTOR);
    return link instanceof HTMLAnchorElement ? link : null;
  }

  function getProductUrl(card) {
    const link = getProductLink(card);
    if (!link?.href) return null;

    try {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) {
        return null;
      }
      return url;
    } catch (_error) {
      return null;
    }
  }

  function getCardImage(card) {
    const activeImage = card?.querySelector(
      'slideshow-slide[aria-hidden="false"] img.product-media__image'
    );
    if (activeImage instanceof HTMLImageElement) return activeImage;

    const customImage = card?.querySelector('[data-gullyo-card-image]');
    if (customImage instanceof HTMLImageElement) return customImage;

    const fallbackImage = card?.querySelector(
      '.card-gallery img.product-media__image, .gullyo-product-card__image'
    );
    return fallbackImage instanceof HTMLImageElement ? fallbackImage : null;
  }

  function warmImage(card, productUrl, urgent) {
    if (warmedImages.has(productUrl.href)) return;

    const source = getCardImage(card);
    if (!source) return;

    const fallbackSrc = source.currentSrc || source.src;
    if (!fallbackSrc) return;

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = urgent ? 'high' : 'low';

    if (source.srcset) {
      image.srcset = source.srcset;
      image.sizes = PDP_IMAGE_SIZES;
    }

    image.src = fallbackSrc;
    warmedImages.set(productUrl.href, image);
  }

  function prefetchDocument(productUrl) {
    if (warmedDocuments.has(productUrl.href)) return;

    if (HTMLScriptElement.supports?.('speculationrules')) {
      const rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({
        prefetch: [
          {
            source: 'list',
            urls: [productUrl.href],
            eagerness: 'immediate',
          },
          ],
        });
      document.head.append(rules);
      warmedDocuments.set(productUrl.href, rules);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = productUrl.href;
    link.fetchPriority = 'low';
    document.head.append(link);
    warmedDocuments.set(productUrl.href, link);
  }

  function warmProduct(card, urgent = false) {
    const productUrl = getProductUrl(card);
    if (!productUrl) return;

    card.dataset.gullyoPrefetched = 'true';
    prefetchDocument(productUrl);
    warmImage(card, productUrl, urgent);
    rememberWarmedProduct(productUrl);
  }

  function scheduleTouchWarmups() {
    if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    let warmupCount = 0;
    const observedCards = new WeakSet();
    let mutationObserver = null;

    const stopObserving = () => {
      observer.disconnect();
      mutationObserver?.disconnect();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || warmupCount >= MAX_TOUCH_WARMUPS) continue;

          observer.unobserve(entry.target);
          warmupCount += 1;

          const warm = () => warmProduct(entry.target);
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(warm, { timeout: 1200 });
          } else {
            window.setTimeout(warm, 250);
          }
        }

        if (warmupCount >= MAX_TOUCH_WARMUPS) stopObserving();
      },
      { rootMargin: '180px 0px' }
    );

    const observeCard = (card) => {
      if (!(card instanceof Element) || observedCards.has(card)) return;
      observedCards.add(card);
      observer.observe(card);
    };

    const observeCardTree = (node) => {
      if (!(node instanceof Element)) return;
      if (node.matches(CARD_SELECTOR)) observeCard(node);
      node.querySelectorAll(CARD_SELECTOR).forEach(observeCard);
    };

    document.querySelectorAll(CARD_SELECTOR).forEach(observeCard);

    mutationObserver = new MutationObserver((records) => {
      if (warmupCount >= MAX_TOUCH_WARMUPS) return;
      records.forEach((record) => record.addedNodes.forEach(observeCardTree));
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      const card = getCard(event.target);
      const relatedTarget = event.relatedTarget;
      if (!card || (relatedTarget instanceof Node && card.contains(relatedTarget))) return;

      window.clearTimeout(hoverTimer);
      hoverCard = card;
      hoverTimer = window.setTimeout(() => {
        warmProduct(card);
        hoverCard = null;
      }, HOVER_DELAY_MS);
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      const card = getCard(event.target);
      const relatedTarget = event.relatedTarget;
      if (
        !card ||
        card !== hoverCard ||
        (relatedTarget instanceof Node && card.contains(relatedTarget))
      ) {
        return;
      }

      window.clearTimeout(hoverTimer);
      hoverCard = null;
    },
    { passive: true }
  );

  document.addEventListener('focusin', (event) => {
    const card = getCard(event.target);
    if (card) warmProduct(card);
  });

  document.addEventListener(
    'pointerdown',
    (event) => {
      const card = getCard(event.target);
      const link =
        event.target instanceof Element ? event.target.closest(PRODUCT_LINK_SELECTOR) : null;
      if (card && link && card.contains(link)) warmProduct(card, true);
    },
    { capture: true, passive: true }
  );

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-gullyo-swatch]')) {
      return;
    }

    const card = getCard(event.target);
    if (card) queueMicrotask(() => warmProduct(card));
  });

  function startTouchWarmups() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(scheduleTouchWarmups, { timeout: 2200 });
    } else {
      window.setTimeout(scheduleTouchWarmups, 900);
    }
  }

  if (document.readyState === 'complete') {
    startTouchWarmups();
  } else {
    window.addEventListener('load', startTouchWarmups, { once: true });
  }
})();
